import React, { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { getSubmit } from '@ui-seeker/utils'

import { AppAcrissAutocomplete, AppAcrissGroupsAutocomplete, AppCategoriesAutocomplete } from '@components'
import { BACKEND_APIS, SERVICES_TYPE } from '@constants'
import { dsColorNew } from '@palette'
import {
	CARS, GROUPS_HEADER, NO_CARS, NO_GROUPS_TEXT, carsKinds, carsLabels, carsMenuTab, carsOn, carsReplaceNote, carsUnder, pickCars,
} from '../@services/cars'
import { KindPillsField } from '@components'


const GROUPS_URL = `${BACKEND_APIS.CRUD}/user/${SERVICES_TYPE.ACRISS_GROUP}?selectorFormat=true`

// The company's groups (E6: every user), to know whether there are any and to name the picked ones
function useGroupOptions() {
	const [options, setOptions] = useState(null)
	useEffect(() => {
		let alive = true
		getSubmit(GROUPS_URL).then(res => alive && setOptions(Array.isArray(res) ? res : [])).catch(() => alive && setOptions([]))
		return () => { alive = false }
	}, [])
	return options
}

const groupOption = (props, o) => <Box component="li" {...props} key={o.value} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important' }}>
	<span>{o.label}</span>
	{!!o.acriss?.length && <Typography component="small" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{o.acriss.join(', ')}</Typography>}
</Box>

/**
 * "Cars" of a BI dashboard (CU-869fmhmx8 E1, E6, E7): one field in the band; its menu picks the kind with pills
 * (Acriss · Category · Acriss Group, opening on Acriss) and the values, one kind at a time. It asks the server again.
 * Under the field, what it means — for groups, their codes as the server resolved them (`resolved` = the answer's
 * `cars`). Since CU-869fmjzrh S2 it is a wrapper of the shared `KindPillsField`.
 */
export default function CarsField({ view, resolved, onChange, onOpenSettings }) {
	const groupOptions = useGroupOptions()

	const labels = carsLabels(view, { groups: resolved?.groups || [], options: groupOptions || [] })
	const kinds = carsKinds({ groupsCount: groupOptions ? groupOptions.length : null })

	const clear = () => onChange({ ...NO_CARS, carsIds: [] })

	const renderKind = (tab, close) => {
		const values = view.cars === tab ? view.carsIds : []
		const autocomplete = {
			value: values, onChange: e => onChange(pickCars(view, tab, e.target.value || [])), multiple: true, limitTags: 3, fullWidth: true, openOnFocus: true,
			ListboxProps: { style: { maxHeight: 260 } },
		}
		const settings = () => { close(); onOpenSettings?.() }
		if (tab === CARS.GROUP && kinds.find(k => k.value === tab)?.empty) {
			return <Typography sx={{ fontSize: 13, color: dsColorNew.textDark, lineHeight: 1.5 }}>
				{NO_GROUPS_TEXT.replace(/Settings › Acriss groups\.$/, '')}
				<Button variant="text" size="small" onClick={settings} sx={{ textTransform: 'none', p: '0 2px', minWidth: 0, verticalAlign: 'baseline' }}>Settings › Acriss groups</Button>.
			</Typography>
		}
		return <>
			{tab === CARS.GROUP && <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 1 }}>
				{GROUPS_HEADER.split(' · ')[0]} · <Button variant="text" size="small" onClick={settings} sx={{ textTransform: 'none', p: 0, minWidth: 0, fontSize: 11, letterSpacing: 0.6, fontWeight: 600 }}>{GROUPS_HEADER.split(' · ')[1]}</Button>
			</Typography>}
			{tab === CARS.GROUP && <AppAcrissGroupsAutocomplete {...autocomplete} placeholder="All groups" renderOption={groupOption}/>}
			{tab === CARS.ACRISS && <AppAcrissAutocomplete {...autocomplete}/>}
			{tab === CARS.CATEGORY && <AppCategoriesAutocomplete {...autocomplete}/>}
		</>
	}

	return <KindPillsField
		label="Cars" allText="All cars" clearLabel="All cars" pillsLabel="Filter cars by"
		on={carsOn(view)} labels={labels} under={carsUnder(view, resolved)}
		kinds={kinds} openTab={() => carsMenuTab(view)}
		renderKind={renderKind} noteFor={tab => carsReplaceNote(view, tab, labels)}
		onClear={clear}
	/>
}
